<template>
	<div class="economic-box">
		<div class="out-box">
			<div v-for="(item, index) in infos" :key="index" class="in-box">
				<div class="top">
					<div class="count">
						{{ toFixed(infosData[item.props]) }}
					</div>
					<div class="unit">{{ item.unit }}</div>
				</div>
				<div class="bottom">{{ item.name }}</div>
			</div>
		</div>
		<div v-if="hasTax" class="out-box">
			<div v-for="(item, index) in taxs" :key="index" class="in-box">
				<div class="top">
					<div class="count">{{ toFixed(taxsData[item.props]) }}</div>
					<div class="unit">{{ item.unit }}</div>
				</div>
				<div class="bottom">{{ item.name }}</div>
			</div>
		</div>
		<div style="margin-top: 32px" class="detail-infos">计算得出经济分析结果如下：</div>
		<div class="out-box">
			<div v-for="(item, index) in analysis" :key="index" class="in-box">
				<div class="top">
					<div class="count">
						{{ toFixed(analysisData[item.props], item.toFixed) }}
					</div>
					<div class="unit">{{ item.unit }}</div>
				</div>
				<div class="bottom">{{ item.name }}</div>
			</div>
		</div>
		<div style="margin-top: 32px" class="detail-infos">计算得出经济分析结果如下：</div>
		<div class="info-table mt0">
			<div class="title">历年放电收益</div>
			<history-income-table :yearProfitInfos="yearProfitInfosData"></history-income-table>
		</div>
		<div class="info-table">
			<div class="title">投资方现金流</div>
			<investor-cash-flow :investorCashFlows="investorCashFlowsData" :hasTax="hasTax"></investor-cash-flow>
		</div>
	</div>
</template>

<script setup>
import {ref, defineProps, watch} from "vue";
import {toFixed} from "@/plugins/index.js";

import HistoryIncomeTable from "../../../../../components/HistoryIncomeTable.vue";
import InvestorCashFlow from "../../../../../components/InvestorCashFlow.vue";

const props = defineProps({
	economicInfo: {
		type: Object,
		default: () => {
			return null;
		},
	},
});

const infos = [
	{
		name: "储能单价",
		unit: "元/Wh",
		props: "unitPrice",
	},
	{
		name: "年运营成本占建设成本",
		unit: "%",
		props: "operationCost",
	},
	{
		name: "储能寿命",
		unit: "年",
		props: "lifeYear",
	},
	{
		name: "年运行天数",
		unit: "天",
		props: "yearRunDays",
	},
	{
		name: "年衰减率",
		unit: "%",
		props: "annualDecayRate",
	},
	{
		name: "前5年投资方分成",
		unit: "%",
		props: "earlyPercent",
	},
	{
		name: "5年后投资方分成",
		unit: "%",
		props: "laterPercent",
	},
];

const infosData = ref({
	unitPrice: undefined,
	operationCost: undefined,
	lifeYear: undefined,
	yearRunDays: undefined,
	annualDecayRate: undefined,
	earlyPercent: undefined,
	yearThreshold: undefined,
	laterPercent: undefined,
});

const taxs = [
	{
		name: "进项税：建设期设备材料费税率",
		unit: "%",
		props: "buildInputTax",
	},
	{
		name: "进项税：经营期运营费税率",
		unit: "%",
		props: "operationInputTax",
	},
	{
		name: "销项税：现金流入税率",
		unit: "%",
		props: "outputTax",
	},
	{
		name: "设备折旧年限",
		unit: "年",
		props: "yearDepreciation",
	},
	{
		name: "所得税税率",
		unit: "%",
		props: "incomeTax",
	},
];
const taxsData = ref({
	buildInputTax: undefined,
	operationInputTax: undefined,
	outputTax: undefined,
	yearDepreciation: undefined,
	incomeTax: undefined,
});
const hasTax = ref(true);

// 表格数据
const yearProfitInfosData = ref([]);
const investorCashFlowsData = ref([]);

const analysis = [
	{
		name: "单位容量日均收益",
		unit: "元/(天*kWh)",
		props: "unitCapacityDayProfit",
		toFixed: 4,
	},
	{
		name: "日均收益",
		unit: "元/天",
		props: "dayProfit",
		toFixed: 2,
	},
	{
		name: "内部收益率 IRR",
		unit: "%",
		props: "irr",
		toFixed: 2,
	},
	{
		name: "储能度电成本 LCOS",
		unit: "元/kWh",
		props: "lcos",
		toFixed: 4,
	},
];

const analysisData = ref({
	dayProfit: undefined,
	unitCapacityDayProfit: undefined,
	irr: undefined,
	lcos: undefined,
});

// 生成顶部数据
const makeTopInfos = (info = {}) => {
	const {economicParam, taxParam} = info;
	if (economicParam) {
		infosData.value = economicParam;
	}

	if (taxParam) {
		hasTax.value = true;
		taxsData.value = taxParam;
	} else {
		hasTax.value = false;
	}
};

// 生成底部数据
const makeBottomInfos = (info = {}) => {
	const {economicAnalysisResult, yearProfitInfos, investorCashFlows} = info;
	if (economicAnalysisResult) {
		analysisData.value = economicAnalysisResult;
	}

	if (yearProfitInfos) {
		yearProfitInfosData.value = yearProfitInfos;
	}
	if (investorCashFlows) {
		investorCashFlowsData.value = investorCashFlows;
	}
};

watch(
	() => props.economicInfo,
	newVal => {
		if (newVal) {
			console.log(newVal);
			const {economicAnalysisVO, economicAnalysisResultVO} = newVal;
			// 生成顶部信息栏
			makeTopInfos(economicAnalysisVO);
			// 生成底部信息
			makeBottomInfos(economicAnalysisResultVO);
		}
	},
	{immediate: true, deep: true}
);
</script>
<style lang="less" scoped>
.economic-box {
	.out-box {
		height: 88px;
		background: #e9eff633;
		border: 1px solid #a2abbb66;
		border-radius: 4px;
		padding: 16px 0;
		display: flex;
		margin-bottom: 16px;
		.in-box {
			flex: 1;
			border-right: 1px solid #a2abbb66;

			&:last-child {
				border-right: unset;
			}

			.top {
				color: #000000;

				font-family: PingFangSC;

				line-height: 28px;
				letter-spacing: 0;

				display: flex;
				align-items: center;
				justify-content: center;
				.count {
					font-size: 20px;
					font-weight: 600;
				}
				.unit {
					font-size: 14px;
					font-weight: 400;
					margin-left: 2px;
				}
			}
			.bottom {
				height: 22px;
				opacity: 0.85;
				font-family: PingFangSC-Regular;
				font-weight: 400;
				font-size: 14px;
				color: #000000;
				letter-spacing: 0;
				text-align: center;
				line-height: 22px;
			}
		}
	}

	.info-table {
		margin-top: 32px;
		.title {
			height: 24px;
			opacity: 0.85;
			font-family: PingFangSC-Medium;
			font-weight: 600;
			font-size: 16px;
			color: #000000;
			letter-spacing: 0;
			line-height: 24px;
			margin-bottom: 8px;
		}
	}
	.mt0 {
		margin-top: 0px;
	}
}
</style>
